/**
 * Cell renderer for the alerts table "Triggered" column (OI-CDS-04).
 *
 * Value shape (composed by buildCrisisTriggeredCellValue):
 *   { triggered: true | false | null, engagements: [{ resourceKey, timestamp }] }
 *
 * Three renderings for three states:
 *   true  — green check + "Yes"; hovering shows every tap as
 *           "<resource label> · <local date-time>". Wording never
 *           claims a call happened — a tap only means the link was
 *           triggered.
 *   false — plain "No": the resources were shown and not tapped. A
 *           real answer, so it gets a word, but a quiet one — not
 *           tapping is not a finding to colour.
 *   null  — muted em dash: the assessment never showed the resources,
 *           so there is nothing to answer. This used to be the only
 *           non-"Yes" rendering, and swallowed every "No".
 */
import { useSelector } from 'react-redux';
import { Tooltip } from '@shared/ui';
import { getLocalizedDateWithTime } from '@shared/lib/localize';
import { getCrisisResourceLabel } from '../model/crisisResource';

const CheckIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const CrisisTriggeredCell = ({ value }) => {
  const { tables } = useSelector(state => state.i18n);

  if (value?.triggered === false) {
    return <span style={{ color: 'var(--qhs-text-2)' }}>{tables?.crisisTriggeredNo || 'No'}</span>;
  }

  if (value?.triggered !== true) {
    return <span style={{ color: 'var(--qhs-text-3)' }}>&mdash;</span>;
  }

  const details = (
    <div>
      {(value.engagements || []).map((engagement, index) => (
        <div key={`${engagement.resourceKey}-${engagement.timestamp}-${index}`}>
          {getCrisisResourceLabel(engagement.resourceKey)}
          {engagement.timestamp ? ` · ${getLocalizedDateWithTime(engagement.timestamp)}` : ''}
        </div>
      ))}
    </div>
  );

  return (
    <Tooltip title={details}>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          color: 'var(--qhs-st-normal-tx)',
          fontWeight: 600,
          cursor: 'default',
        }}
      >
        <CheckIcon />
        {tables?.crisisTriggeredYes || 'Yes'}
      </span>
    </Tooltip>
  );
};

export default CrisisTriggeredCell;
